import { createContext, useCallback, useMemo, useState, ReactNode } from 'react';

import { LocalStorageConfig, TimeModeType } from '../../constants/Constants';
import { TimeMode } from '../../types/TimeMode';

interface TimeModeContextType {
  timeMode: TimeMode;
  isUtc: boolean;
  setTimeMode: (timeMode: TimeMode) => void;
}

// UTC is the default: every timestamp the endpoints return is UTC, so showing it
// as-is is the honest default and matches what the columns rendered before the
// toggle became app-wide. The user's choice is remembered per browser.
export const defaultTimeMode: TimeMode = TimeModeType.Utc as TimeMode;

const readStoredTimeMode = (): TimeMode => {
  try {
    const stored = localStorage.getItem(LocalStorageConfig.TIME_MODE);

    return stored === TimeModeType.Local || stored === TimeModeType.Utc ? (stored as TimeMode) : defaultTimeMode;
  } catch (err) {
    console.error('Failed to read the stored time mode', err);
    return defaultTimeMode;
  }
};

export const TimeModeContext = createContext<TimeModeContextType>({
  timeMode: defaultTimeMode,
  isUtc: true,
  setTimeMode: () => undefined,
});

export function TimeModeProvider({ children }: { children: ReactNode }) {
  const [timeMode, setTimeModeState] = useState<TimeMode>(readStoredTimeMode);

  const setTimeMode = useCallback((next: TimeMode) => {
    setTimeModeState(next);

    try {
      localStorage.setItem(LocalStorageConfig.TIME_MODE, next);
    } catch (err) {
      console.error('Failed to persist the time mode', err);
    }
  }, []);

  const value = useMemo(() => ({ timeMode, isUtc: timeMode === TimeModeType.Utc, setTimeMode }), [timeMode, setTimeMode]);

  return <TimeModeContext.Provider value={value}>{children}</TimeModeContext.Provider>;
}
